/**
 * Generic add/edit page: loads the record when editing, validates with the shared
 * schema, saves via POST/PUT and navigates to the saved record. Every entity form
 * uses this so behaviour (validation, errors, cancel, loading) is consistent.
 */
import type { Schema } from "@shared/validation.ts";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router";
import { api } from "../../lib/api.ts";
import { type Check, useSchemaForm } from "../../lib/forms.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { ErrorAlert, Loading, PageHeader, SubmitButton } from "../ui/kit.tsx";
import { type AnyForm, FormError } from "./fields.tsx";

type Rec = Record<string, unknown>;

interface EntityFormProps<T> {
  schema: Schema<T>;
  checks?: Check<T>[];
  /** Collection endpoint, e.g. "/api/properties". */
  resource: string;
  id?: string | null;
  defaults: Rec;
  /** Map a loaded record into form values (defaults to the record itself). */
  toForm?: (record: Rec) => Rec;
  /** Adjust the validated value before sending. */
  toBody?: (value: T) => unknown;
  title: string;
  breadcrumbs?: Array<{ label: string; to?: string }>;
  redirect: (saved: Rec) => string;
  /** Runs after a successful save (e.g. upload attachments) before redirecting. */
  afterSave?: (saved: Rec) => Promise<void>;
  cancelTo: string;
  submitLabel?: string;
  children: (form: AnyForm, record: Rec | null) => ReactNode;
}

export function EntityForm<T>(props: EntityFormProps<T>) {
  const existing = useApi<Rec>(props.id ? `${props.resource}/${props.id}` : null);
  if (props.id && existing.isLoading) return <Loading />;
  if (props.id && existing.error) return <ErrorAlert error={existing.error} />;
  const record = existing.data ?? null;
  const values = record
    ? { ...props.defaults, ...(props.toForm ? props.toForm(record) : record) }
    : props.defaults;
  return <InnerForm {...props} record={record} initial={values} />;
}

function InnerForm<T>(props: EntityFormProps<T> & { record: Rec | null; initial: Rec }) {
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const { form, formError, submit } = useSchemaForm(props.schema, props.initial, props.checks);
  return (
    <>
      <PageHeader title={props.title} breadcrumbs={props.breadcrumbs} />
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={submit(async (value) => {
            const body = props.toBody ? props.toBody(value) : value;
            const saved = props.id
              ? await api.put<Rec>(`${props.resource}/${props.id}`, body)
              : await api.post<Rec>(props.resource, body);
            if (props.afterSave) await props.afterSave(saved);
            await invalidate();
            navigate(props.redirect(saved));
          })}
        >
          <Card>
            <Card.Body>
              <FormError message={formError} />
              {props.children(form, props.record)}
            </Card.Body>
            <Card.Footer className="bg-white d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light" onClick={() => navigate(props.cancelTo)}>
                Cancel
              </button>
              <SubmitButton busy={form.formState.isSubmitting}>
                {props.submitLabel ?? (props.id ? "Save changes" : "Save")}
              </SubmitButton>
            </Card.Footer>
          </Card>
        </form>
      </FormProvider>
    </>
  );
}
